import type { PaymentPlan, PaymentPlanDiscount, PrepayPeriodOrContinue, PriceVO } from '@ctct/domain-layer';

import type { TranslationGetter } from '../../../types';
import type { PaymentPlanDiscountDisplayVerbose, PrepayOptionsStrategy } from '../types';

export class PayflowPrepayOptionsStrategy implements PrepayOptionsStrategy {
  constructor(protected opts: { t: TranslationGetter; allowSelectionOfShorterTerm: boolean; isStripe?: boolean }) {}

  public getAvailablePeriodOptions(paymentPlan: PaymentPlan): PaymentPlanDiscount[] {
    return paymentPlan.getPlanDiscounts();
  }

  public getCompactPeriodDisplayParameters(planDiscounts: PaymentPlanDiscount[], paymentPlan: PaymentPlan) {
    return planDiscounts.map(({ discountPeriod, discountDescription, discountPercent }) => {
      let text: string;

      if (discountPeriod === 0) {
        text = this.opts.t('application-layer.pricing-configurator.continue-plan');
      } else if (discountPeriod === 1) {
        text = discountDescription;
      } else if (this.opts.isStripe) {
        // Use subscription language for Stripe
        text =
          discountPeriod === 12
            ? this.opts.t('application-layer.pricing-configurator.annual-subscription')
            : this.opts.t('application-layer.pricing-configurator.subscription-x-months', { months: discountPeriod });
      } else {
        // Use prepay language for BaaS
        text = this.opts.t('application-layer.pricing-configurator.prepay-x-months', { months: discountPeriod });
      }

      const textSuffix = discountPercent
        ? this.opts.t('application-layer.pricing-configurator.save-x-percent', { percent: discountPercent })
        : undefined;

      const disabled = !this.opts.allowSelectionOfShorterTerm && discountPeriod < paymentPlan.getCurrentPaymentPeriod();

      return {
        id: String(discountPeriod),
        text,
        textSuffix,
        disabled,
      };
    });
  }

  public getVerbosePeriodDisplayParameters(
    planDiscounts: PaymentPlanDiscount[],
    paymentPlan: PaymentPlan,
    planBaseMonthlyPrice: null | { value: PriceVO; currency: string }
  ): PaymentPlanDiscountDisplayVerbose[] {
    const currentPeriod = paymentPlan.getCurrentPaymentPeriod();

    return planDiscounts.map(({ discountPeriod, discountPercent }) => {
      const value = String(discountPeriod);
      const disabled = !this.opts.allowSelectionOfShorterTerm && discountPeriod < currentPeriod;

      const prepaySavings = planBaseMonthlyPrice
        ? planBaseMonthlyPrice.value
            .multiply((discountPercent / 100) * discountPeriod)
            .format(planBaseMonthlyPrice.currency)
        : null;

      // Use -stripe suffix for Stripe label keys (only periods 6+ have -stripe variants)
      const labelSuffix = this.opts.isStripe && discountPeriod >= 6 ? '-stripe' : '';
      const labelI18nKey = `application-layer.payment-plans.options.${value}.label${labelSuffix}`;

      return {
        id: String(discountPeriod),
        labelI18nKey,
        descriptionI18nKey: planBaseMonthlyPrice
          ? `application-layer.payment-plans.options.${value}.description-with-savings`
          : `application-layer.payment-plans.options.${value}.description`,
        discountPercent,
        savings: prepaySavings,
        isCurrent: currentPeriod === discountPeriod,
        disabled,
      };
    });
  }

  public getPreselectedPeriod(paymentPlan: PaymentPlan) {
    return String(paymentPlan.getDefaultCustomerPaymentPeriod()) as PrepayPeriodOrContinue;
  }

  public getIsPrepayPeriodValid(paymentPlan: PaymentPlan, prepayPeriod: string): boolean {
    return paymentPlan.getPlanDiscounts().some((discount) => String(discount.discountPeriod) === prepayPeriod);
  }
}
